import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import React from 'react'

import type { InvoiceDetail, TransactionDetail } from '../../../../api/useFreight/types'
import { defaultDateDef, defaultNumberDef } from '../../../_SiteWide/CellTemplates/typeColumnDefs'
import {
  getActionsColumnDef,
  getAmountColumnDef,
  getBookTypeColumnDef,
  getCounterpartyColumnDef,
  getDestinationsColumnDef,
  getLineItemTypeColumnDef,
  getOriginsColumnDef,
  getProductGroupColumnDef,
  getProductNameColumnDef,
  getRateColumnDef,
  getStatusColumnDef,
  getSubtypeColumnDef,
  getTotalColumnDef,
  getUomColumnDef,
} from './utils'
import { ActionsCellRenderer, OrderNumberRenderer } from '../../FreightLineItems'
import { dayjsWithTimezone, formatters } from '@/utils/intl/dateTimeFormatters'
import { TransactionErrorCellRenderer } from '../../FreightLineItems/components/renderers/OrderErrorCellRenderer'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Tag, Tooltip } from 'antd'
import { IconUsers } from '@tabler/icons-react'
import { customFLIFilters } from '../../FreightLineItems/components/filters'

/**
 * Column definitions for the flat view of line items grid
 *
 * @returns Column definitions for the flat view
 */
export const getColumnDefs = (): ColDef<TransactionDetail | InvoiceDetail>[] => [
  // Book type column
  {
    ...getBookTypeColumnDef(),
  },

  // Delivery time column
  {
    headerName: 'DELIVERY TIME',
    field: 'date',
    valueFormatter: ({ value, data }) =>
      formatters.SHORT_DATE_TIME(dayjsWithTimezone(value, data?.date_local), data?.date_local),
    initialSort: 'desc',
    flex: 1,
    ...defaultDateDef('date'),
  },

  // Order column
  {
    headerName: 'ORDER',
    field: 'order_number',
    width: 136,
    minWidth: 136,
    ...customFLIFilters,
    cellRenderer: OrderNumberRenderer,
  },

  {
    headerName: 'PURCHASE ORDER',
    field: 'po',
  },

  // Counterparty column
  {
    ...getCounterpartyColumnDef(),
  },

  // Status column
  {
    ...getStatusColumnDef(),
  },
  {
    headerName: 'ERROR',
    field: 'error.type',
    cellRenderer: TransactionErrorCellRenderer,
  },
  {
    headerName: 'ERROR MESSAGE',
    field: 'error.message',
    hide: true,
    suppressColumnsToolPanel: true, // added only for downloading purposes
  },

  {
    ...getOriginsColumnDef(),
  },
  {
    ...getDestinationsColumnDef(),
  },
  // Product group column
  {
    ...getProductGroupColumnDef(),
  },

  // Product name column
  {
    ...getProductNameColumnDef(),
  },

  // Type column
  {
    ...getLineItemTypeColumnDef(),
  },

  // Subtype column
  {
    ...getSubtypeColumnDef(),
  },

  // Rate column
  {
    ...getRateColumnDef(),
    filter: 'agNumberColumnFilter',
  },

  // Amount column
  {
    ...getAmountColumnDef(),
    ...defaultNumberDef('amount'),
    valueFormatter: (params) => {
      if (!params.value) return ''
      return params.value.toString()
    },
    filterValueGetter: (params: ValueGetterParams<TransactionDetail | InvoiceDetail>) => {
      if (!params.data) return null
      return params.data.amount_override !== null && params.data.amount_override !== undefined
        ? params.data.amount_override
        : params.data.amount
    },
  },

  // UOM column
  {
    ...getUomColumnDef(),
  },

  // Total column
  {
    ...getTotalColumnDef(),
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => {
      if (!params.value) return ''
      return params.value.toString()
    },
    filterValueGetter: (params: ValueGetterParams<TransactionDetail | InvoiceDetail>) => {
      if (!params.data) return null
      return params.data.total_override !== null && params.data.total_override !== undefined
        ? params.data.total_override
        : params.data.total
    },
  },

  // Actions column
  {
    ...getActionsColumnDef(),
    cellStyle: { display: 'flex', flex: 1, padding: 0 },
    cellRenderer: (params: ICellRendererParams<TransactionDetail | InvoiceDetail>) => {
      return <ActionsCellRenderer data={params.data as InvoiceDetail} api={params.api} context={params.context} />
    },
  },
]
